import { useMemo } from 'react'

import { matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import { groupProcessingFees, matchFilterService } from '@functions/processingFee'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { ProcessingFeeModel } from '@models/types'

import type { EntityOptions } from '@redux/modules/types'

const watchEntityKeys = ['processingFees']

export type ProcessingFeesFilters = {
  debtor_id?: number,
  debtor_type?: 'Agency' | 'Brand' | 'Provider',
  global?: boolean,
  service_id?: number,
  string?: string,
}

interface UseEntitiesOptions {
  entityKey?: string,
  filters?: ProcessingFeesFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useProcessingFees(options: UseEntitiesOptions = {}) {
  const { filters = {}, requestOptions } = options
  const {
    debtor_id: filterDebtorId,
    debtor_type: filterDebtorType,
    global: filterGlobal,
    string: filterString,
  } = filters

  const {
    updatedEntities: { processingFees: processingFeesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const {
    agencies, brands, processingFees, providers, serviceProducts,
  } = useSelector(reduxState => reduxState.entities)

  const filteredProcessingFees = useMemo((): ProcessingFeeModel[] => {
    const filtered = Object.values(processingFees).filter((processingFee) => {
      const {
        billable_id, billable_type, debtor_id, debtor_type, global,
      } = processingFee

      const serviceProduct = billable_type === 'ServiceProduct' ? serviceProducts[billable_id] : undefined
      const { id, reference_title, title } = serviceProduct || {}

      // String Filter matches on Service Product (billable) attributes only
      const matchId = id?.toString() === filterString
      const matchReferenceTitle = matchFilterString(reference_title, filterString)
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchReferenceTitle || matchTitle

      const matchDebtorId = filterDebtorId ? filterDebtorId === debtor_id : !debtor_id
      const matchDebtorType = filterDebtorType ? filterDebtorType === debtor_type : true
      const matchGlobal = global === !!filterGlobal
      const matchService = matchFilterService({ filters, processingFee, serviceProducts })

      return matchDebtorId && matchDebtorType && matchGlobal && matchService && matchString
    })

    return sortArrayBy(filtered, 'asc', ({ billable_id }) => serviceProducts[billable_id]?.title)
  }, [processingFeesUpdatedAt, JSON.stringify(filters)])

  const filteredProcessingFeesCount = filteredProcessingFees.length
  const hasFilteredProcessingFees = !!filteredProcessingFeesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredProcessingFeesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingProcessingFees } = useReduxAction(
    'processingFees',
    'loadProcessingFees',
    {
      ...filtersWithOffset,
      ...requestOptions,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => !!performHttpRequests && !loading,
    },
  )

  const groupedProcessingFees = groupProcessingFees(filteredProcessingFees, serviceProducts)

  const filterAgency = filterDebtorType === 'Agency' && filterDebtorId ? agencies[filterDebtorId] : undefined
  const filterBrand = filterDebtorType === 'Brand' && filterDebtorId ? brands[filterDebtorId] : undefined
  const filterProvider = filterDebtorType === 'Provider' && filterDebtorId ? providers[filterDebtorId] : undefined

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filterAgency,
    filterBrand,
    filteredProcessingFees,
    filteredProcessingFeesCount,
    filterProvider,
    groupedProcessingFees,
    hasFilteredProcessingFees,
    loading: loadingProcessingFees,
  }
}

export default useProcessingFees
